import { ChartLineUp } from "@phosphor-icons/react";
import { EmptyState } from "../shared/EmptyState";

const LineupMini = ({ label, block, testId }) => (
  <div data-testid={testId} className="rounded-lg border border-white/10 bg-eb-bg p-3">
    <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{label}</span>
    {block?.status === "available" ? (
      <ul data-testid={`${testId}-entries`} className="mt-1.5 space-y-0.5 font-mono text-xs text-zinc-300">
        {block.entries.map((e) => (
          <li key={`${e.batting_order}-${e.player_id}`} className="flex justify-between gap-2">
            <span>
              {e.batting_order}. {e.full_name || "Unknown"}
            </span>
            <span className="text-zinc-500">{e.position}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p data-testid={`${testId}-unavailable`} className="mt-1.5 font-mono text-xs text-zinc-400">
        {block?.status === "insufficient_data" ? "Insufficient Data" : "Unavailable"} &mdash; {block?.reason || "Not yet confirmed."}
      </p>
    )}
  </div>
);

export const PredictionCard = ({ prediction, awayLabel, homeLabel, testId }) => {
  if (!prediction) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <EmptyState icon={ChartLineUp} title="Prediction Unavailable" description="Loading prediction data." testId={`${testId}-empty`} />
      </div>
    );
  }

  const ok = prediction.status === "ok";
  const asOfLabel = prediction.as_of
    ? new Date(prediction.as_of).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "—";

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <p className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Full Game Moneyline</p>
      {ok ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-white/10 bg-eb-bg p-4 text-center">
            <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{awayLabel || "Away"} Win Probability</span>
            <p data-testid={`${testId}-away-probability`} className="mt-1 font-heading text-3xl text-white">
              {(prediction.away_probability * 100).toFixed(1)}%
            </p>
          </div>
          <div className="rounded-lg border border-white/10 bg-eb-bg p-4 text-center">
            <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{homeLabel || "Home"} Win Probability</span>
            <p data-testid={`${testId}-home-probability`} className="mt-1 font-heading text-3xl text-white">
              {(prediction.home_probability * 100).toFixed(1)}%
            </p>
          </div>
        </div>
      ) : (
        <EmptyState
          icon={ChartLineUp}
          title="Insufficient Data For A Prediction"
          description={prediction.reason || "Not enough data to generate a baseline prediction yet."}
          testId={`${testId}-insufficient`}
        />
      )}
      <p data-testid={`${testId}-meta`} className="font-mono text-[11px] text-zinc-400">
        Status: {prediction.status} &middot; Model: {prediction.model_version} &middot; Features: {prediction.feature_version} &middot; As of{" "}
        {asOfLabel}
        {ok && !prediction.pitcher_adjustment_applied ? " (no starting-pitcher adjustment applied — ERA unavailable for one or both sides)" : ""}
      </p>
      <div data-testid={`${testId}-lineups`} className="flex flex-col gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Confirmed Lineups (display only — not fed into the formula)</span>
        <div className="grid grid-cols-2 gap-4">
          <LineupMini label={awayLabel || "Away"} block={prediction.away_lineup} testId={`${testId}-lineup-away`} />
          <LineupMini label={homeLabel || "Home"} block={prediction.home_lineup} testId={`${testId}-lineup-home`} />
        </div>
      </div>
      <p className="font-mono text-[10px] text-zinc-500">
        Baseline formula (Log5 + Pythagorean run-differential + starting-pitcher ERA) &mdash; not a trained/calibrated model. Moneyline
        itself has no odds, edge or betting recommendation (see the Full Game O/U section above for that).
      </p>
    </div>
  );
};
